import { type FC, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents, urlTransform } from "../../../utils/markdownComponents";
import { Attachment, User } from "../../../core/types";
import MessageAttachment from "./MessageAttachment";
import TypewriterEffect from "../../../components/TypewriterEffect";
import LogoIcon from "../../../components/icons/LogoIcon";
import { useScroll } from "../../../contexts/useScroll";
import { useTranslation } from "../../../contexts/useTranslation";
import { Slot, Templated } from "../../../templating";
import { RichMediaScope } from "../../../contexts/RichMediaContext";

interface ChatMessageListAssistantItemProps {
  messageId: number;
  threadId: number;
  message: string;
  /** When set, the bubble represents a human support-agent reply (avatar +
   * name shown). When null, an AI reply (logo, no name). */
  sender: User | null;
  attachment?: Attachment | null;
  /** Whether the message should animate in via typewriter (AI replies that
   * are still streaming in). Human agent replies arrive fully formed via
   * ActionCable and never typewriter. */
  isTyping?: boolean;
  /** Transient assistant UI (such as submit_rating) must not influence sizing. */
  reportRichMedia?: boolean;
  onFinished?: () => void;
  /** Extra content rendered below the body — e.g. rating buttons inside
   * a feedback message. */
  children?: ReactNode;
}

/**
 * Unified bubble for every `role: "assistant"` message — human support
 * agents AND AI replies. The two cases share layout and the
 * `lastbot-message-bubble-assistant` container template, but the inner
 * slots are split along the sender boundary so they can be overridden
 * independently:
 *
 *   When sender is null (AI):
 *     - <Slot name="assistant-avatar">    → LogoIcon by default.
 *     - <Slot name="assistant-body">      → TypewriterEffect / Markdown.
 *     - <Slot name="assistant-attachment"> → only if attachment present.
 *     - <Slot name="assistant-feedback">  → only when children passed (submit_rating
 *       messages pass a RatingInput). Without this slot, fully-templated bubbles
 *       would silently drop the rating UI.
 *     - (no name slot — AI bot doesn't show a name)
 *
 *   When sender is a User (human agent):
 *     - <Slot name="agent-avatar">        → avatar_url image or initial-circle.
 *     - <Slot name="agent-name">          → first name.
 *     - <Slot name="agent-body">          → Markdown (no typewriter for humans).
 *     - <Slot name="agent-attachment">    → only if attachment present.
 *
 * Embedders override either set via per-slot templates
 * (`<template id="lastbot-assistant-avatar">…</template>` for AI,
 * `<template id="lastbot-agent-avatar">…</template>` for humans) or
 * restructure the whole bubble via
 * `<template id="lastbot-message-bubble-assistant">`.
 */
const ChatMessageListAssistantItem: FC<ChatMessageListAssistantItemProps> = ({
  messageId,
  threadId,
  message,
  sender,
  attachment = null,
  isTyping = false,
  reportRichMedia = true,
  onFinished,
  children,
}) => {
  const { translate } = useTranslation();
  const { keepAnchor } = useScroll();
  const supportAgentName = sender?.first_name
    ? sender.first_name
    : translate("support_agent");

  // Slot names are sender-conditional so embedders can override the AI and
  // human paths independently (e.g. only template `lastbot-agent-avatar`
  // to give human reps a custom photo placeholder while leaving the AI
  // LogoIcon at its default).
  const avatarSlot = sender ? "agent-avatar" : "assistant-avatar";
  const bodySlot = sender ? "agent-body" : "assistant-body";
  const attachmentSlot = sender ? "agent-attachment" : "assistant-attachment";

  return (
    <RichMediaScope
      threadId={threadId}
      messageId={messageId}
      settled={!isTyping}
      enabled={reportRichMedia}
    >
    <Templated id="lastbot-message-bubble-assistant">
      <div className="flex gap-x-4" data-testid="assistant-message" data-message-id={messageId}>
        <div className="shrink-0">
          <Slot name={avatarSlot}>
            {sender ? (
              sender.avatar_url ? (
                <img src={sender.avatar_url} alt={supportAgentName} className="size-[30px] rounded-full" />
              ) : (
                <div className="flex size-[30px] items-center justify-center rounded-full bg-lastbot-icon-muted font-semibold text-lastbot-on-inverse uppercase">
                  {supportAgentName.charAt(0)}
                </div>
              )
            ) : (
              <LogoIcon />
            )}
          </Slot>
        </div>
        {/*
          Body column matches the pre-unification AI-bot bubble exactly so
          existing visual snapshots still match. `flex flex-col gap-y-6`
          gives the 1.5rem rhythm between body + attachment + children that
          the AI path always had. Top padding (`pt-0.5`) lives on the
          markdown wrap, not the body column — same place the old code put
          it — so vertical alignment relative to the avatar is unchanged.
          For human-agent replies, the optional name renders ahead of the
          body inside the same flex column; the gap-y-6 spacing applies
          equally there.
        */}
        <div className="flex-1 flex flex-col gap-y-6 break-words min-w-0">
          {sender && (
            <div className="font-semibold text-lastbot-on-surface pb-1">
              <Slot name="agent-name">{supportAgentName}</Slot>
            </div>
          )}
          {/*
            Slot WRAPS the styled markdown container (rather than wrapping
            only the markdown content inside it). This keeps the rendered
            DOM identical to the pre-unification AI-bubble structure: the
            `<div class="leading-… formatted-content">` is a real block box
            whose direct children are the markdown `<p>` / `<ol>` / `<ul>`
            elements. With the wrap inside the styled div, the `display:
            contents` slot marker sat between the block container and its
            first markdown child, and browser margin-collapse drifted by a
            few pixels per element — visible in visual regression. Slot
            outside the styled div avoids that.
          */}
          <Slot name={bodySlot}>
            <div className="leading-[1.6] text-lastbot-on-surface pt-0.5 formatted-content">
              {isTyping ? (
                <TypewriterEffect
                  content={message}
                  // Each reveal tick re-applies the reading anchor instead of
                  // chasing the bottom: a long answer is read from its start
                  // (LAS-1381). The anchor lets go the moment the reader
                  // scrolls.
                  onChange={keepAnchor}
                  onFinished={() => onFinished?.()}
                  className=""
                />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={urlTransform}>
                  {message}
                </ReactMarkdown>
              )}
            </div>
          </Slot>
          {attachment && (
            <div className="pt-4 pb-2">
              <Slot name={attachmentSlot}>
                <MessageAttachment attachment={attachment} />
              </Slot>
            </div>
          )}
          {children && (
            <Slot name="assistant-feedback">{children}</Slot>
          )}
        </div>
      </div>
    </Templated>
    </RichMediaScope>
  );
};

export default ChatMessageListAssistantItem;
